import { keyframes } from "@mui/material/styles";
import { makeStyles } from "components/mui-core";
import { Tooltip } from "components-redesign/Tooltip/Tooltip";
import { AllDownloadsModalTrigger } from "components/RouteAgnosticDialogs/AllCsvDownloadsDialog";
import { differenceInMilliseconds } from "date-fns";
import { ReactComponent as DownloadStatusCompletedIcon } from "icons/analytics/downloadStatus/downloadStatusCompleted.svg";
import { ReactComponent as DownloadStatusFailedIcon } from "icons/analytics/downloadStatus/downloadStatusFailed.svg";
import { ReactComponent as DownloadStatusIdleIcon } from "icons/analytics/downloadStatus/downloadStatusIdle.svg";
import { ReactComponent as DownloadStatusLoadingArrowIcon } from "icons/analytics/downloadStatus/downloadStatusLoadingArrow.svg";
import { ReactComponent as DownloadStatusLoadingCircleIcon } from "icons/analytics/downloadStatus/downloadStatusLoadingCircle.svg";
import { Milliseconds } from "model/modelTypes";
import React from "react";
import { useSelector } from "react-redux";
import { analyticsCsvDownloadsSlice } from "redux/csvDownloads/analyticsCsvDownloadsSlice";
import {
  getAllCsvDownloadsList,
  isAllDownloadsModalOpened,
} from "redux/csvDownloads/selectors";
import { trafficCsvDownloadsSlice } from "redux/csvDownloads/trafficCsvDownloadsSlice";
import { useAppDispatch } from "redux/store";

const spin360 = keyframes({
  from: {
    transform: "rotate(0deg)",
  },
  to: {
    transform: "rotate(360deg)",
  },
});

const useStyles = makeStyles()({
  spinAnimation: {
    animationName: `${spin360}`,
    animationDuration: "10s",
    animationTimingFunction: "linear",
    animationIterationCount: "infinite",
  },
});

type TStatus = "idle" | "loading" | "failed" | "completed";
const IDLE_STATUS_DELAY: Milliseconds = 8 * 1000;
const FIVE_SECONDS: Milliseconds = 5 * 1000;

const StatusIcon = ({ status }: { status: TStatus }) => {
  const { classes } = useStyles();

  switch (status) {
    case "idle":
      return (
        <DownloadStatusIdleIcon
          width={24}
          height={24}
        />
      );
    case "loading":
      return (
        <div
          style={{
            position: "relative",
            width: 24,
            height: 24,
            marginBottom: "6px",
          }}>
          <DownloadStatusLoadingCircleIcon
            width={24}
            height={24}
            className={classes.spinAnimation}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
            }}
          />
          <DownloadStatusLoadingArrowIcon
            width={24}
            height={24}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
            }}
          />
        </div>
      );
    case "completed":
      return (
        <DownloadStatusCompletedIcon
          width={24}
          height={24}
        />
      );
    case "failed":
      return (
        <DownloadStatusFailedIcon
          width={24}
          height={24}
        />
      );
    default:
      return (
        <DownloadStatusIdleIcon
          width={24}
          height={24}
        />
      );
  }
};

function AllDownloadsButton({
  onActive,
}: {
  onActive?: (active: boolean) => void;
}) {
  const dispatch = useAppDispatch();
  const downloadsList = useSelector(getAllCsvDownloadsList);
  const [status, setStatus] = React.useState<TStatus>("idle");
  const isModalOpened = useSelector(isAllDownloadsModalOpened);

  React.useEffect(() => {
    onActive?.(isModalOpened || false);
  }, [isModalOpened, onActive]);

  React.useEffect(() => {
    dispatch(analyticsCsvDownloadsSlice.actions.loadList());
    dispatch(trafficCsvDownloadsSlice.actions.loadList());
  }, [dispatch]);

  React.useEffect(() => {
    let timeoutId;
    const mostRecentDownload = downloadsList[0];
    const statusChangeMsAgo = differenceInMilliseconds(
      Date.now(),
      mostRecentDownload?.statusModifiedTS ?? 0
    );
    const isDownloadInProgress = downloadsList.some(
      ({ status }) => status === "loading"
    );

    const getStatus = (): TStatus => {
      if (!mostRecentDownload) {
        return "idle";
      }
      if (mostRecentDownload.status === "loading") {
        return "loading";
      }
      const statusChangedRecently = statusChangeMsAgo < IDLE_STATUS_DELAY;
      if (mostRecentDownload.status === "failed" && statusChangedRecently) {
        return "failed";
      }
      if (mostRecentDownload.status === "completed" && statusChangedRecently) {
        return "completed";
      }
      return "idle";
    };

    const newStatus = getStatus();
    setStatus(newStatus);

    if (isDownloadInProgress && statusChangeMsAgo < FIVE_SECONDS) {
      const delayLeft = FIVE_SECONDS - statusChangeMsAgo;
      timeoutId = setTimeout(() => {
        setStatus("loading");
      }, delayLeft);
      return () => clearTimeout(timeoutId);
    }

    if (newStatus === "failed" || newStatus === "completed") {
      const delayLeft = IDLE_STATUS_DELAY - statusChangeMsAgo;
      if (delayLeft > 0) {
        timeoutId = setTimeout(() => {
          setStatus("idle");
        }, delayLeft);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [downloadsList]);

  return (
    <Tooltip
      title="CSV downloads"
      placement="bottom">
      <AllDownloadsModalTrigger>
        <StatusIcon status={status} />
      </AllDownloadsModalTrigger>
    </Tooltip>
  );
}
export default AllDownloadsButton;
